import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail, Send, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  category: "user" | "admin" | "system";
}

const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: "notify-profile-published",
    name: "Profile Approved",
    description: "Sent when admin approves a user's profile",
    category: "user",
  },
  {
    id: "notify-admin-new-profile",
    name: "New Profile Pending (Admin)",
    description: "Sent to admins when a new user registers",
    category: "admin",
  },
  {
    id: "notify-connection-request",
    name: "Connection Request",
    description: "Sent to mentor when entrepreneur requests connection",
    category: "user",
  },
  {
    id: "notify-connection-rejected",
    name: "Connection Rejected",
    description: "Sent to entrepreneur when connection is declined",
    category: "user",
  },
  {
    id: "notify-call-cancelled",
    name: "Call Cancelled",
    description: "Sent when a scheduled call is cancelled",
    category: "user",
  },
  {
    id: "notify-discussion-reply",
    name: "Discussion Reply",
    description: "Sent when someone replies to a discussion",
    category: "user",
  },
  {
    id: "notify-help-request",
    name: "Help Request (Admin)",
    description: "Sent to admins/instructors for new help requests",
    category: "admin",
  },
  {
    id: "notify-new-message",
    name: "New Message",
    description: "Sent when user receives a new message",
    category: "user",
  },
  {
    id: "notify-times-submitted",
    name: "Times Submitted",
    description: "Sent when mentor submits availability times",
    category: "user",
  },
  {
    id: "send-scheduling-email",
    name: "Call Scheduled",
    description: "Sent when a call is scheduled with Zoom link",
    category: "user",
  },
  {
    id: "send-community-announcement-email",
    name: "Community Announcement",
    description: "Sent for community-wide announcements",
    category: "system",
  },
];

export function EmailTestingManager() {
  const { user } = useAuth();
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendingTemplate, setSendingTemplate] = useState<string | null>(null);
  const [lastResults, setLastResults] = useState<Record<string, "success" | "error" | null>>({});

  const handleSendTest = async (templateId: string) => {
    if (!recipientEmail) {
      toast.error("Please enter a recipient email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingTemplate(templateId);
    setLastResults((prev) => ({ ...prev, [templateId]: null }));

    try {
      const { data, error } = await supabase.functions.invoke("send-test-email", {
        body: {
          template_type: templateId,
          recipient_email: recipientEmail,
        },
      });

      if (error) throw error;

      setLastResults((prev) => ({ ...prev, [templateId]: "success" }));
      toast.success(`Test email sent successfully to ${recipientEmail}`);
    } catch (error: any) {
      console.error("Error sending test email:", error);
      setLastResults((prev) => ({ ...prev, [templateId]: "error" }));
      toast.error(`Failed to send test email: ${error.message}`);
    } finally {
      setSendingTemplate(null);
    }
  };

  const handleSendAll = async () => {
    if (!recipientEmail) {
      toast.error("Please enter a recipient email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    for (const template of EMAIL_TEMPLATES) {
      await handleSendTest(template.id);
      // Small delay between sends to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  };

  const getCategoryColor = (category: EmailTemplate["category"]) => {
    switch (category) {
      case "user":
        return "bg-primary/10 text-primary";
      case "admin":
        return "bg-warning/10 text-warning";
      case "system":
        return "bg-accent/10 text-accent";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getResultIcon = (templateId: string) => {
    const result = lastResults[templateId];
    if (result === "success") {
      return <CheckCircle2 className="h-4 w-4 text-success" />;
    } else if (result === "error") {
      return <XCircle className="h-4 w-4 text-destructive" />;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Template Testing
          </CardTitle>
          <CardDescription>
            Test all email templates by sending sample emails to a specified address.
            Each test email will be prefixed with [TEST] in the subject line.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Test emails use sample data and are clearly marked as tests. They will be sent
              through the production email system (Resend) using the same templates as real emails.
            </AlertDescription>
          </Alert>

          <div className="flex gap-4 items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="recipient-email">Recipient Email Address</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="admin@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              onClick={handleSendAll}
              disabled={!recipientEmail || sendingTemplate !== null}
            >
              {sendingTemplate ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send All Tests
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {EMAIL_TEMPLATES.map((template) => (
          <Card key={template.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{template.name}</CardTitle>
                <div className="flex items-center gap-2">
                  {getResultIcon(template.id)}
                  <Badge variant="secondary" className={getCategoryColor(template.category)}>
                    {template.category}
                  </Badge>
                </div>
              </div>
              <CardDescription className="text-xs">{template.description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between">
                <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                  {template.id}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendTest(template.id)}
                  disabled={!recipientEmail || sendingTemplate !== null}
                >
                  {sendingTemplate === template.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
